/**
 * Storefront GraphQL API 
 * @module api/storefrontApi
 */

class StorefrontApi {
  /**
   * Constructor
   * Uses global variables injected from Liquid
   */
  constructor() {
    this.endpoint = window.__STOREFRONT_API_ENDPOINT__;
    this.token = window.__STOREFRONT_ACCESS_TOKEN__;

    if (!this.endpoint || !this.token) {
      console.warn('Storefront API configuration is missing');
    }
  }

  /**
   * Execute GraphQL query
   * @param {string} query - GraphQL query string
   * @param {Object} variables - Query variables
   * @returns {Promise<Object>} Data part of API response
   * @throws {Error} API error or GraphQL error
   */
  async query(query, variables = {}) {
    if (!this.endpoint || !this.token) {
      throw new Error('Storefront API is not configured');
    }

    const response = await fetch(this.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.token,
      },
      body: JSON.stringify({ query, variables }),
    });

    if (!response.ok) {
      throw new Error(`Storefront API error: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (result.errors && result.errors.length > 0) {
      const errorMessages = result.errors.map(e => e.message).join(', ');
      throw new Error(`GraphQL error: ${errorMessages}`);
    }

    return result.data;
  }

  /**
   * Get articles by tags (sorted by publication date, newest first)
   * @param {string[]} brandTags - Array of brand tags
   * @param {number} [limit=32] - Number of articles to retrieve
   * @returns {Promise<Article[]>} Array of article objects
   *
   * @description
   * - If brandTags is empty: Query with `tag:search_news` only (retrieve all news)
   * - If brandTags has 1 item: Query with `tag:search_news AND tag:xxx`
   * - If brandTags has multiple items: Query with `tag:search_news AND (tag:xxx OR tag:yyy)`
   *
   * Note: Flagship stores always carry specific brands, so brandTags should not be empty.
   * The specification is to return all news if brandTags is empty.
   */
  async getArticlesByTags(brandTags, limit = 32) {
    const GET_ARTICLES_QUERY = `
      query GetArticlesByTags($first: Int!, $query: String!) {
        articles(
          first: $first
          sortKey: PUBLISHED_AT
          reverse: true
          query: $query
        ) {
          nodes {
            id
            title
            handle
            publishedAt
            tags
            image {
              url
              altText
              width
              height
            }
            blog {
              handle
            }
            onlineStoreUrl
            metafield(namespace: "im-ft", key: "img_border") {
              value
            }
          }
        }
      }
    `;

    // Build query string
    // Format: "tag:search_news AND (tag:brand1 OR tag:brand2)"
    let tagQuery;
    if (brandTags.length === 0) {
      tagQuery = 'tag:search_news';
    } else if (brandTags.length === 1) {
      tagQuery = `tag:search_news AND tag:"${brandTags[0]}"`;
    } else {
      const orCondition = brandTags.map(t => `tag:"${t}"`).join(' OR ');
      tagQuery = `tag:search_news AND (${orCondition})`;
    }

    const data = await this.query(GET_ARTICLES_QUERY, {
      first: limit,
      query: tagQuery,
    });

    return data.articles.nodes;
  }
}

export const storefrontApi = new StorefrontApi();
